import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  metricDefinitionsApi,
  type CreateMetricDefinitionInput,
  type MetricDefinitionDetail,
  type MetricDefinitionListItem,
  type Predicate,
  type UpdateMetricDefinitionInput,
} from '@/lib/api/metric-definitions.api';

const QUERY_KEY = ['metric-definitions'];

export function useMetricDefinitionList(tag?: string) {
  return useQuery<MetricDefinitionListItem[]>({
    queryKey: tag ? [...QUERY_KEY, { tag }] : QUERY_KEY,
    queryFn: () => metricDefinitionsApi.list(tag),
  });
}

export function useMetricDefinitionDetail(key: string | undefined) {
  return useQuery<MetricDefinitionDetail>({
    queryKey: [...QUERY_KEY, key],
    queryFn: () => metricDefinitionsApi.getByKey(key!),
    enabled: !!key,
  });
}

export function useCreateMetricDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMetricDefinitionInput) => metricDefinitionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['metric-options'] });
    },
  });
}

export function useUpdateMetricDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateMetricDefinitionInput }) =>
      metricDefinitionsApi.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['metric-options'] });
    },
  });
}

export function useDeleteMetricDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => metricDefinitionsApi.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['metric-options'] });
    },
  });
}

export function useTestPredicate() {
  return useMutation({
    mutationFn: (input: { predicate: Predicate; executedAtFrom?: string; executedAtTo?: string }) =>
      metricDefinitionsApi.test(input),
  });
}
